'use client';

import {
  ISalePageDetailV2Response,
  ISalePageDetailV2ResponseProduct,
} from '@/api/types';
import { isRandomMediaProduct } from '@/entities/product/utils';
import { hasRandomMediaProduct } from '@/entities/salePage/utils';
import { Column, Element, Row } from '@vuddy/components';
import clsx from 'clsx';
import { Key, ReactNode } from 'react';
import { SalePagePurchaseGuideSections } from './PurchaseGuideSection';
import * as styles from './randomMediaGuide.css';

interface RandomMediaGuideProps {
  salePage?: Partial<ISalePageDetailV2Response>;
}

export const RandomMediaGuide = ({ salePage }: RandomMediaGuideProps) => {
  if (!hasRandomMediaProduct(salePage!)) return null;

  // 테이블 행 렌더링을 위한 공통 함수
  const renderTableRow = ({
    leftContent,
    rightContent,
    isHeader = false,
    key,
  }: {
    leftContent: ReactNode;
    rightContent: ReactNode;
    isHeader: boolean;
    key?: Key;
  }) => (
    <Row
      key={key}
      className={clsx({
        [styles.tableRow]: true,
        [styles.tableHeader]: isHeader,
      })}>
      <Row
        width="100%"
        paddingX="padding_3xs"
        paddingY="padding_2xs"
        alignItems="center"
        typography="captionBase"
        color="secondary">
        {leftContent}
      </Row>
      <Row
        width="100%"
        paddingX="padding_3xs"
        paddingY="padding_2xs"
        alignItems="center"
        typography="captionBase"
        fontWeight={isHeader ? undefined : 'bold'}
        color="secondary">
        {rightContent}
      </Row>
    </Row>
  );

  // 확률 테이블 렌더링 함수를 내부 함수로 분리
  const renderProbabilityTable = (
    product: ISalePageDetailV2ResponseProduct
  ) => (
    <Column key={product.id} gap="gap_small">
      <Element typography="bodySmall">{product.name} 굿즈 확률 정보</Element>
      <Column width="100%" backgroundColor="primary">
        {renderTableRow({
          leftContent: '버디카드명',
          rightContent: '확률(%)',
          isHeader: true,
          key: 'header',
        })}
        {product.randomPickRules.map(rule =>
          renderTableRow({
            leftContent: rule.mediaItemName,
            rightContent: `${rule.percentage}%`,
            isHeader: false,
            key: rule.mediaItemId,
          })
        )}
      </Column>
    </Column>
  );

  const randomMediaRandomPickRuleSections = [
    {
      description: [
        '아래 확률은 상품 구성 아이템의 개별 획득 확률을 나타내며, 모든 확률은 소수점 이하 셋째 자리에서 반올림된 값입니다. 실제 지급된 아이템의 확률은 아래 표와 일치합니다.',
        ...(salePage?.products ?? [])
          ?.filter(isRandomMediaProduct)
          .map(renderProbabilityTable),
      ],
    },
  ];

  const randomMediaSections = [
    {
      description: [
        '랜덤 미디어 상품에서 각 아이템의 획득 확률은 고정된 값으로 유지됩니다.',
        '모든 아이템은 동일한 조건에서 뽑힐 확률을 보장합니다.',
        '본 상품의 확률 정보는 관련 법령 및 가이드라인을 준수하여 제공됩니다.',
        '랜덤 지급 상품 특성상, 동일한 상품이 중복 지급될 수 있습니다.',
      ],
    },
  ];

  return (
    <>
      <SalePagePurchaseGuideSections
        title="확률 정보"
        sections={randomMediaRandomPickRuleSections}
      />
      <SalePagePurchaseGuideSections
        title="랜덤 상품 구매시 유의사항"
        sections={randomMediaSections}
      />
    </>
  );
};
